import React, { useLayoutEffect, useRef } from 'react';
import { LoadingOutlined, SendOutlined } from '@ant-design/icons';
import styled from 'styled-components';

import { intelligenceScrollbar } from 'containers/Intelligence/theme/scrollbar';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';
import usePerx3Redesign from 'hooks/usePerx3Redesign';

import { TOrbit } from '../i18n/TModules';
import {
  OrbitBodyText,
  OrbitMutedText,
  OrbitTextButton,
  orbitTokens,
  orbitType,
  perx3OrbitPrimaryButton
} from '../styles';
import useOrbitComposer, { ORBIT_QUESTION_MAX_LENGTH } from '../useOrbitComposer';

export { ORBIT_QUESTION_MAX_LENGTH };

const TEXTAREA_MAX_HEIGHT_PX = 96;

// div.composer 6034:4869 gaps the input and the fine print by 8.
const Root = styled.div`
  > * + * {
    margin-top: 8px;
  }
`;

// div.input-wrap 6034:4870: 52 tall at r12 under a 1px rule. The 10 / 9 padding centres the 30
// send button on that height, and the one-row field is the same 30.
const Form = styled.form`
  display: flex;
  align-items: flex-end;
  box-sizing: border-box;
  min-height: 52px;
  padding: 10px 9px;
  background: ${orbitTokens.surfaceAlt};
  border: 1px solid ${orbitTokens.border};
  border-radius: 12px;

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 8px;
  }

  &:focus-within {
    border-color: ${orbitTokens.textMuted};
  }
`;

// input#ask 6034:4871 pads 0 / 8 around a 19 line of Inter Regular 13 (#AEBBD0 placeholder).
const TextArea = styled.textarea`
  flex: 1 1 auto;
  min-width: 0;
  max-height: ${TEXTAREA_MAX_HEIGHT_PX}px;
  margin: 0;
  padding: 5.5px 8px;
  border: none;
  background: transparent;
  color: ${orbitTokens.text};
  font-family: inherit;
  font-size: 13px;
  line-height: 19px;
  resize: none;
  overflow-y: auto;

  /* A grown draft would otherwise sit under the bar. */
  ${intelligenceScrollbar('y', { pad: true })}

  &::placeholder {
    color: ${orbitTokens.textSecondary};
  }

  &:focus {
    outline: none;
  }

  &:read-only {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const SubmitButton = styled.button<{ $perx3: boolean }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  width: 30px;
  height: 30px;
  padding: 0;
  border: none;
  border-radius: 8px;
  background: ${orbitTokens.action};
  color: ${orbitTokens.onAction};
  cursor: pointer;
  font-size: 13.33px;

  &:focus-visible {
    outline: 2px solid ${orbitTokens.text};
    outline-offset: 1px;
  }

  &:disabled {
    background: ${orbitTokens.disabledFill};
    color: ${orbitTokens.disabledInk};
    cursor: not-allowed;
  }

  /* PERX3-REDESIGN round 7: the composer's send is a solid-fill primary, so it takes the black
     treatment. The block's hover excludes :disabled, so the inert state keeps its own fill. */
  ${({ $perx3 }) => $perx3 && perx3OrbitPrimaryButton}
`;

/*
  The 2000-character cap is stated only when it is BROKEN (user, 2026-09-14: "remove word counter
  from the bottom"). No `maxLength` on the textarea either: that would silently swallow the tail of
  a pasted question instead of saying why it cannot be sent. `canSubmit` below is what enforces it.
*/
const TooLong = styled.p`
  margin: 0;
  color: ${orbitTokens.negative};
  ${orbitType.helping}
`;

const AskError = styled(OrbitBodyText)`
  display: flex;
  align-items: baseline;
  color: ${orbitTokens.negative};
  ${orbitType.helping}

  /* Safari < 14.1 has no flex gap (conventions/components/cross-browser.md § 5). */
  > * + * {
    margin-left: 8px;
  }
`;

const AskErrorText = styled.span`
  flex: 1 1 auto;
  min-width: 0;
`;

// The panel's own text button, set on the error line's type rather than the 12/16 default.
const AskRetryButton = styled(OrbitTextButton)`
  ${orbitType.helping}
`;

const OrbitComposer: React.FC = () => {
  const { t } = useMultiLanguageTranslation();
  const perx3 = usePerx3Redesign();
  const {
    draft,
    isAsking,
    overLimit,
    canSubmit,
    submit,
    handleSubmit,
    handleKeyDown,
    handleChange,
    askErrorText
  } = useOrbitComposer();
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  // Keyed on the value, not written from `onChange`: the height is derived state, and clearing the
  // draft after a successful ask fires no change event, so an `onChange`-only resize would leave an
  // empty textarea pinned at the height of the question that was just sent.
  useLayoutEffect(() => {
    const textArea = textAreaRef.current;

    if (textArea === null) {
      return;
    }

    textArea.style.height = 'auto';

    /*
      An empty field is the one row rows={1} already gives it, written as no inline height at all
      rather than as a measurement. The effect first runs as the panel mounts, while the sidebar
      wrapper is still 0 wide and OrbitPanel's max-width: 100% has collapsed the field with it; the
      scrollHeight read in that state came back over the cap and pinned the empty composer at 96px
      until the user's first keystroke re-ran it (PLA-8844 parity F-02).
    */
    if (draft === '') {
      textArea.style.height = '';

      return;
    }

    textArea.style.height = `${Math.min(textArea.scrollHeight, TEXTAREA_MAX_HEIGHT_PX)}px`;
  }, [draft]);

  const placeholder = t(`${TOrbit}.composer.placeholder`);
  return (
    <Root data-testid="orbit-composer">
      <Form onSubmit={handleSubmit}>
        <TextArea
          ref={textAreaRef}
          rows={1}
          value={draft}
          placeholder={placeholder}
          aria-label={placeholder}
          aria-invalid={overLimit}
          aria-describedby={overLimit ? 'orbit-composer-status' : undefined}
          /*
            `readOnly`, not `disabled`: disabling the element that currently has focus blurs it to
            `document.body`, which drops a keyboard user out of the panel the moment they ask and
            never gives the focus back when the field re-enables.
          */
          readOnly={isAsking}
          data-testid="orbit-composer-input"
          onChange={handleChange}
          onKeyDown={handleKeyDown}
        />
        <SubmitButton
          type="submit"
          $perx3={perx3}
          aria-label={t(`${TOrbit}.actions.ask`)}
          aria-busy={isAsking}
          disabled={!canSubmit}
          data-testid="orbit-composer-submit"
        >
          {isAsking ? (
            <LoadingOutlined spin aria-hidden="true" />
          ) : (
            <SendOutlined aria-hidden="true" />
          )}
        </SubmitButton>
      </Form>
      {overLimit ? (
        <TooLong id="orbit-composer-status" role="alert" data-testid="orbit-composer-too-long">
          {t(`${TOrbit}.composer.tooLong`, { max: ORBIT_QUESTION_MAX_LENGTH })}
        </TooLong>
      ) : null}
      {askErrorText !== null ? (
        <AskError role="alert" data-testid="orbit-composer-error">
          <AskErrorText>{askErrorText}</AskErrorText>
          <AskRetryButton
            type="button"
            disabled={!canSubmit}
            data-testid="orbit-composer-retry"
            onClick={submit}
          >
            {t(`${TOrbit}.actions.retry`)}
          </AskRetryButton>
        </AskError>
      ) : null}
      <OrbitMutedText data-testid="orbit-composer-disclaimer">
        {t(`${TOrbit}.notice.disclaimer`)}
      </OrbitMutedText>
    </Root>
  );
};

export default OrbitComposer;
